import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GetBlogs } from "@/components/api/queries";
import LoadingSkeleton from "@/components/LoadingSkeleton/LoadingSkeleton";
import BlogItemCard from "./BlogItemCard";
import EmailSubscribe from "../Subscribe/EmailSubcribe";
import Footer from "@/components/footer/footer";
import { Section } from "@/components/ui/section";
import { ScrollContainer } from "@/components/ui/scroll-container";
import { Search, BookOpen, Filter } from "lucide-react";
import { z } from "zod";
import { schemaBlogs, type Blogs as BlogsResponse } from "@/components/api/zod-types";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";



export default function Blogs() {
  const location = useLocation();

  useEffect(() => {
      window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const [searchQuery, setSearchQuery] = useState("");

  const blogsQuery = useQuery({
    queryKey: ["blogs"],
    staleTime: 60 * 1000 * 30, // 5min cache time
    queryFn: async () => {
      const res = await GetBlogs();
      try {
        // Parse and validate the response data
        const parsed = schemaBlogs.parse(res.data);
        if (parsed.statuscode === 404 || parsed.status === "error") {
          throw new Error(parsed.status === "error" ? "Failed to fetch blogs" : "No blogs found");
        }
        return parsed;
      } catch (error) {
        if (error instanceof z.ZodError) {
          throw new Error("Invalid blog data received from server");
        }
        throw error;
      }
    },
  });
  
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Filter blogs only if we have valid data
  const filteredBlogs = blogsQuery.data?.results
    ? blogsQuery.data.results.filter((item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : [];  
  

  // Show error state if the query failed
  if (blogsQuery.isError) {
    return (
      <ScrollContainer snapType="y" snapBehavior="mandatory">
        <Section
          scrollSnap={true}
          variant="light"
          padding="xl"
          className="bg-gradient-to-br from-gray-50 via-white to-blue-50/30"
        >
          <div className="max-w-7xl mx-auto text-center py-32">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Failed to load articles</h3>
            <p className="text-gray-600 mb-6">
              {blogsQuery.error instanceof Error ? blogsQuery.error.message : "An error occurred while loading the articles."}
            </p>
            <button
              onClick={() => blogsQuery.refetch()}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-harvey-red hover:bg-harvey-red/90 transition-colors duration-200"
            >
              Try again
            </button>
          </div>
        </Section>
      </ScrollContainer>
    );
  }

  return (
    <ScrollContainer snapType="y" snapBehavior="mandatory">
      {/* Header Section */}
      <Section
        scrollSnap={true}
        overlayColor="transparent"
        variant="light"
        padding="xl"
        className="bg-gradient-to-br from-gray-50 via-white to-blue-50/30 pt-16"
      >
        <div className="max-w-7xl mx-auto">
          {/* Header with Search */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-12 pt-8">
            <div className="flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-harvey-red rounded-xl flex items-center justify-center shadow-lg">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900">
                  Blog <span className="text-gradient-red">Articles</span>
                </h1>
              </div>
              <p className="text-lg text-gray-600 max-w-2xl text-left">
                Stay informed with the latest industry insights, trends, and expert perspectives on commercial technology solutions.
              </p>
            </div>

            {/* Modern Search Bar */}
            <div className="relative w-full lg:w-auto lg:min-w-80">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>              
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="text-black w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-harvey-red/20 focus:border-harvey-red transition-all duration-200 shadow-sm hover:shadow-md"
              />
              {searchQuery && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <Filter className="h-4 w-4 text-harvey-red" />
                </div>
              )}
            </div>
          </div>

          {/* Stats/Info Bar */}
          <div className="flex items-center justify-between bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 shadow-sm mb-8">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600">
                {blogsQuery.data?.results ? (
                  <>
                    <span className="font-semibold text-gray-900">{filteredBlogs?.length || 0}</span> of{" "}
                    <span className="font-semibold text-gray-900">{blogsQuery.data.results.length}</span> articles
                    {searchQuery && (
                      <span className="ml-2 text-harvey-red">
                        for "{searchQuery}"
                      </span>
                    )}
                  </>
                ) : (
                  "Loading articles..."
                )}
              </div>
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="text-sm text-gray-500 hover:text-harvey-red transition-colors duration-200 flex items-center gap-1"
              >
                Clear search
              </button>
            )}
          </div>

          {/* Content Grid */}
          {blogsQuery.isLoading ? (
            <div className="py-12">
              <LoadingSkeleton message="Loading articles..." />
            </div>
          ) : (
            <div className="grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-8">              
              {blogsQuery.isFetched && filteredBlogs
              .slice()
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((blog) => (
                <BlogItemCard
                  item={blog}
                  type="blog"                  
                  key={blog.id} // We know id exists because of schema validation
                />
              ))}
            </div>
          )}

          {/* No Results Message */}
          {blogsQuery.isFetched && filteredBlogs?.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchQuery ? "No articles found" : "No articles available"}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery
                  ? `We couldn't find any articles matching "${searchQuery}". Try a different search term.`
                  : "There are currently no articles available. Please check back later."
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-harvey-red hover:bg-harvey-red/90 transition-colors duration-200"
                >
                  Show all articles
                </button>
              )}
            </div>
          )}
        </div>
      </Section>
    </ScrollContainer>
  )
};

